import { LOCALE_NAMES } from "@repo/shared";
import type { LocaleCode } from "@repo/shared/types";
import { Languages } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { apiErrorMessage } from "@/lib/api/_client";
import { useCloneFlow } from "@/lib/hooks/use-flows";
import { usePdfTemplatesByFlow } from "@/lib/hooks/use-pdf-templates";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import type { Flow } from "@/lib/types/flow";
import { T } from "./clone-flow-dialog.i18n";

type CloneFlowDialogProps = {
  open: boolean;
  flow: Flow | null;
  onClose: () => void;
};

export function CloneFlowDialog({ open, flow, onClose }: CloneFlowDialogProps) {
  const navigate = useNavigate();
  const t = useT(T);
  const cloneFlow = useCloneFlow();
  const { tenantSupportedLanguages } = useUserLanguage();
  // Fetched only while the dialog is open: the flows list renders it always.
  const { templates } = usePdfTemplatesByFlow(open && flow ? flow.id : null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    language: "",
    translate: true,
    includePdfTemplate: true,
    pdfTemplateName: "",
  });
  const [error, setError] = useState<string | null>(null);

  const sourceLanguage = flow?.language ?? "";
  // QL-334 — a flow has at most one PDF template; offer to bring it along.
  const pdfTemplate = templates[0];
  const languageChanged = !!formData.language && formData.language !== sourceLanguage;
  // A tenant that dropped the source language still lets the clone keep it.
  const languageOptions = [
    ...new Set([sourceLanguage, ...tenantSupportedLanguages].filter(Boolean)),
  ];

  useEffect(() => {
    if (flow) {
      setFormData({
        name: `${flow.name} (copy)`,
        description: flow.description || "",
        language: flow.language ?? "",
        translate: true,
        includePdfTemplate: true,
        pdfTemplateName: "",
      });
    }
    setError(null);
  }, [flow]);

  // The template can arrive after the dialog opens; seed its copy's name then.
  // Runs after the reset above, so a newly opened flow gets its own default.
  useEffect(() => {
    if (flow && pdfTemplate) {
      setFormData((prev) =>
        prev.pdfTemplateName ? prev : { ...prev, pdfTemplateName: `${pdfTemplate.name} (copy)` },
      );
    }
  }, [flow, pdfTemplate]);

  const languageName = (code: string) => LOCALE_NAMES[code as LocaleCode] || code;

  // Closing mid-request (a large plain copy takes a moment) would hide the result:
  // the navigation to the copy and any error land on a dismissed dialog.
  const blockDismissWhilePending = (event: { preventDefault: () => void }) => {
    if (cloneFlow.isPending) event.preventDefault();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!flow) return;
    setError(null);

    try {
      const data = await cloneFlow.mutateAsync({
        id: flow.id,
        name: formData.name,
        description: formData.description || undefined,
        language: formData.language || undefined,
        translate: languageChanged ? formData.translate : undefined,
        includePdfTemplate: pdfTemplate ? formData.includePdfTemplate : undefined,
        pdfTemplateName:
          pdfTemplate && formData.includePdfTemplate ? formData.pdfTemplateName : undefined,
      });
      // QL-334 — a translated copy is made in the background; the list shows it.
      if (data.kind === "queued") {
        toast.info(t.copyQueued);
      } else {
        navigate(`/settings/flows/${data.id}`);
      }
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, t.failedToCloneFlow));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !cloneFlow.isPending) onClose();
      }}
    >
      <DialogContent
        onEscapeKeyDown={blockDismissWhilePending}
        onInteractOutside={blockDismissWhilePending}
      >
        <DialogHeader>
          <DialogTitle>{t.duplicateFlow}</DialogTitle>
          <DialogDescription>{t.cloneFlowDescription}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="clone-flow-name">
                {t.flowName} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="clone-flow-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="clone-flow-description">{t.description}</Label>
              <Textarea
                id="clone-flow-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t.descriptionPlaceholder}
                rows={3}
              />
            </div>

            {languageOptions.length > 1 && (
              <div className="space-y-2">
                <Label htmlFor="clone-flow-language">{t.language}</Label>
                <Select
                  value={formData.language}
                  onValueChange={(language) => setFormData({ ...formData, language })}
                  disabled={cloneFlow.isPending}
                >
                  <SelectTrigger id="clone-flow-language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {languageOptions.map((lang) => (
                      <SelectItem key={lang} value={lang}>
                        {languageName(lang)}
                        {lang === sourceLanguage ? ` (${t.original})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {languageChanged && (
                  <div className="rounded-md border bg-muted/40 p-3 space-y-1.5">
                    <div className="flex items-start gap-2">
                      <Checkbox
                        id="clone-flow-translate"
                        checked={formData.translate}
                        onCheckedChange={(checked) =>
                          setFormData({ ...formData, translate: checked === true })
                        }
                        disabled={cloneFlow.isPending}
                      />
                      <label
                        htmlFor="clone-flow-translate"
                        className="text-sm font-medium leading-none flex items-center gap-1.5"
                      >
                        <Languages className="h-3.5 w-3.5" />
                        {t.translateTo(languageName(formData.language))}
                      </label>
                    </div>
                    <p className="text-xs text-muted-foreground ml-6">
                      {formData.translate ? t.translateHint : t.noTranslateHint}
                    </p>
                  </div>
                )}
              </div>
            )}

            {pdfTemplate && (
              <div className="space-y-1.5">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="clone-flow-include-pdf"
                    checked={formData.includePdfTemplate}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, includePdfTemplate: checked === true })
                    }
                    disabled={cloneFlow.isPending}
                  />
                  <label
                    htmlFor="clone-flow-include-pdf"
                    className="text-sm font-medium leading-none"
                  >
                    {t.includePdfTemplate(pdfTemplate.name)}
                  </label>
                </div>
                <p className="text-xs text-muted-foreground ml-6">{t.includePdfTemplateHint}</p>
                {formData.includePdfTemplate && (
                  <div className="space-y-2 ml-6 pt-1">
                    <Label htmlFor="clone-flow-pdf-name">{t.pdfTemplateName}</Label>
                    <Input
                      id="clone-flow-pdf-name"
                      value={formData.pdfTemplateName}
                      onChange={(e) =>
                        setFormData({ ...formData, pdfTemplateName: e.target.value })
                      }
                      disabled={cloneFlow.isPending}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <DialogCancel disabled={cloneFlow.isPending}>{t.cancel}</DialogCancel>
            <Button type="submit" loading={cloneFlow.isPending}>
              {t.duplicate}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
